import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';
import { E as EmblaCarousel } from './embla-carousel.esm-CCUHK2kO.js';

const SELECTORS = {
  STEP: '[data-calculator-step]',
  FORMULA_CALCULATOR_OPTION: '[data-formula-calculator-option]',
  CONTAINER: '.embla__container',
  VIEWPORT: '.embla__viewport',
  SLIDE: '.embla__slide',
  RESULTS_SLOT: '[data-formula-calculator-results-slot]',
  DYNAMIC_RESULT: '.formula-calculator-dynamic-result-rendered',
  FORMULA_CALCULATOR_RESULT: '[data-formula-calculator-result]',
  NO_RESULT: '[data-formula-calculator-no-result]',
};

/**
 * Option rows inside `<formula-calculator-options>` — Embla carousel + DOM helpers.
 * @extends HTMLElement
 */
class FormulaCalculatorOptions extends HTMLElement {
  static OPTION_SCHEME = {
    selected: 'color-scheme-blue',
    default: 'color-scheme-neutral',
  };

  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  #embla;
  /** @type {HTMLElement[]} */
  #originalSlideTemplates = [];
  /** @type {string[]} */
  #allOptionHandles = [];

  connectedCallback() {
    const container = this.querySelector(SELECTORS.CONTAINER);
    if (!container) return;

    const slides = [...container.querySelectorAll(SELECTORS.SLIDE)];
    this.#originalSlideTemplates = slides.map((slide) => slide.cloneNode(true));
    this.#allOptionHandles = slides
      .map((slide) => slide.querySelector(SELECTORS.FORMULA_CALCULATOR_OPTION))
      .filter((btn) => btn instanceof HTMLElement)
      .map((btn) =>
        FormulaCalculatorOptions.normalizeOptionHandle(btn.dataset.formulaCalculatorOption)
      );

    const viewport = this.querySelector(SELECTORS.VIEWPORT);
    if (!viewport) return;

    this.#embla = EmblaCarousel(viewport, {
      align: 'start',
      loop: false,
      dragFree: true,
      dragThreshold: 10,
      slidesToScroll: 'auto',
      direction: document.dir || 'ltr',
    });
  }

  disconnectedCallback() {
    this.#embla?.destroy();
    this.#embla = undefined;
  }

  /** All option handles for this question (from initial markup). */
  getAllOptionHandles() {
    return [...this.#allOptionHandles];
  }

  /** Restore every option slide and clear selection styling. */
  restoreSlides() {
    const container = this.querySelector(SELECTORS.CONTAINER);
    if (!container) return;

    container.replaceChildren(
      ...this.#originalSlideTemplates.map((template) => template.cloneNode(true))
    );
    this.#reinitEmbla();
  }

  /**
   * Remove slides whose option handle is not in `validHandles`.
   * @param {Set<string>} validHandles
   */
  applyAllowedOptions(validHandles) {
    const container = this.querySelector(SELECTORS.CONTAINER);
    if (!container) return;

    [...container.querySelectorAll(SELECTORS.SLIDE)].forEach((slide) => {
      const btn = slide.querySelector(SELECTORS.FORMULA_CALCULATOR_OPTION);
      if (!(btn instanceof HTMLElement)) {
        slide.remove();
        return;
      }

      const handle = FormulaCalculatorOptions.normalizeOptionHandle(
        btn.dataset.formulaCalculatorOption
      );

      if (!validHandles.has(handle)) {
        slide.remove();
        return;
      }

      FormulaCalculatorOptions.setOptionScheme(btn, false);
    });

    this.#reinitEmbla();
  }

  #reinitEmbla() {
    this.#embla?.reInit();
  }

  /** Normalizes handleized option ids from the theme JSON (`slug` field) and DOM. */
  static normalizeOptionHandle(value) {
    return (value ?? '').toLowerCase().trim();
  }

  /**
   * @param {HTMLElement} btn
   * @param {boolean} selected
   */
  static setOptionScheme(btn, selected) {
    btn.classList.toggle(FormulaCalculatorOptions.OPTION_SCHEME.selected, selected);
    btn.classList.toggle(FormulaCalculatorOptions.OPTION_SCHEME.default, !selected);
    btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
  }

  /**
   * @param {HTMLElement} stepEl
   * @returns {formula-calculator-options | null}
   */
  static getOptionsComponent(stepEl) {
    const el = stepEl.querySelector('formula-calculator-options');
    return el instanceof FormulaCalculatorOptions ? el : null;
  }

  /**
   * @param {HTMLElement} stepEl
   * @returns {HTMLElement[]}
   */
  static listOptionButtons(stepEl) {
    return [...stepEl.querySelectorAll(SELECTORS.FORMULA_CALCULATOR_OPTION)];
  }

  /**
   * @param {HTMLElement} optionEl
   */
  static applySelection(optionEl) {
    const step = optionEl.closest(SELECTORS.STEP);
    if (!step) return;

    step
      .querySelectorAll(SELECTORS.FORMULA_CALCULATOR_OPTION)
      .forEach((btn) => FormulaCalculatorOptions.setOptionScheme(btn, false));
    FormulaCalculatorOptions.setOptionScheme(optionEl, true);
  }

  /**
   * @param {HTMLElement} root
   */
  static resetStyles(root) {
    root
      .querySelectorAll(SELECTORS.FORMULA_CALCULATOR_OPTION)
      .forEach((btn) => FormulaCalculatorOptions.setOptionScheme(btn, false));
  }

  /**
   * Selected visible option handle, or '' if none (no default pre-selection).
   * @param {HTMLElement} stepEl
   * @returns {string}
   */
  static getSelectedOptionHandleForStep(stepEl) {
    const selected = FormulaCalculatorOptions.listOptionButtons(stepEl).find((button) =>
      button.classList.contains(FormulaCalculatorOptions.OPTION_SCHEME.selected)
    );
    return selected
      ? FormulaCalculatorOptions.normalizeOptionHandle(selected.dataset?.formulaCalculatorOption)
      : '';
  }
}

customElements.define('formula-calculator-options', FormulaCalculatorOptions);

/**
 * Formula calculator quiz — options filtered by results; live result / no-result.
 * @extends HTMLElement
 */
class FormulaCalculator extends HTMLElement {
  /** @type {string[]} */
  selectedOptionHandles = [];
  /** @type {string[]} */
  selectedOptionTitles = [];
  /** @type {Object[] | null} */
  calculatorResultsData = null;

  /** @type {(() => void) | null} */
  #unsubscribeVariantChange = null;

  /**
   * @param {unknown} id
   * @returns {string}
   */
  #normVariantId(id) {
    return String(id ?? '').trim();
  }

  /**
   * Metaobject result may include `variants` as number[], or legacy string "[1,2]".
   * Empty list means the result applies to all variants.
   * @param {Record<string, unknown>} result
   * @returns {string[]}
   */
  #parseResultVariantIds(result) {
    const raw = result.variants;
    if (raw === undefined || raw === null) return [];
    if (Array.isArray(raw)) return raw.map((id) => this.#normVariantId(id)).filter(Boolean);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed))
          return parsed.map((id) => this.#normVariantId(id)).filter(Boolean);
      } catch {
        /* ignore */
      }
    }
    return [];
  }

  /**
   * @param {Record<string, unknown>} result
   * @param {string} currentVariantId
   * @returns {boolean}
   */
  #resultAppliesToVariant(result, currentVariantId) {
    const ids = this.#parseResultVariantIds(result);
    if (ids.length === 0 || !currentVariantId) return true;
    return ids.includes(currentVariantId);
  }

  /**
   * @param {Object[]} calculatorResultsData
   * @param {string} currentVariantId
   * @returns {Object[]}
   */
  #filterResultsByVariant(calculatorResultsData, currentVariantId) {
    if (!calculatorResultsData?.length) return [];
    return calculatorResultsData.filter((row) =>
      this.#resultAppliesToVariant(row, currentVariantId)
    );
  }

  /**
   * @returns {string}
   */
  #getCurrentVariantIdFromDom() {
    const input = document.querySelector('product-form input[name="variant-id"]');
    if (!(input instanceof HTMLInputElement)) return '';
    return this.#normVariantId(input.value || input.getAttribute('value') || '');
  }

  /**
   * @param {Object[]} resultSelectedOptions - from result JSON (`slug` is the handleized option id)
   * @param {string[][]} optionHandlesByStep - handles per question from initial markup (all options)
   * @returns {string[] | null} one handle per step, or null if no valid assignment
   */
  #assignOptionHandlesToSteps(resultSelectedOptions, optionHandlesByStep) {
    const stepCount = optionHandlesByStep.length;
    const choiceHandles = (resultSelectedOptions || [])
      .map((option) => FormulaCalculatorOptions.normalizeOptionHandle(option.slug))
      .filter(Boolean);
    if (choiceHandles.length !== stepCount) return null;

    const handleSetsByStep = optionHandlesByStep.map(
      (handles) => new Set(handles.map((h) => FormulaCalculatorOptions.normalizeOptionHandle(h)))
    );

    /**
     * @param {number} slotIndex
     * @param {string[]} pickedHandles
     */
    function assignDepthFirst(slotIndex, pickedHandles) {
      if (slotIndex === stepCount) return pickedHandles;
      for (const choiceHandle of choiceHandles) {
        if (pickedHandles.includes(choiceHandle)) continue;
        if (!handleSetsByStep[slotIndex].has(choiceHandle)) continue;
        const nextAssignment = assignDepthFirst(slotIndex + 1, [...pickedHandles, choiceHandle]);
        if (nextAssignment !== null) return nextAssignment;
      }
      return null;
    }

    return assignDepthFirst(0, []);
  }

  /**
   * @param {string[]} handlesPerStep
   * @param {string[]} prefixHandles
   * @param {number} stepCount
   * @returns {boolean}
   */
  #prefixHandlesMatch(handlesPerStep, prefixHandles, stepCount) {
    return handlesPerStep
      .slice(0, stepCount)
      .every(
        (handle, index) =>
          FormulaCalculatorOptions.normalizeOptionHandle(handle) ===
          FormulaCalculatorOptions.normalizeOptionHandle(prefixHandles[index])
      );
  }

  /**
   * Option handles valid at `stepIndex` given earlier steps match `prefixHandles` (each aligned to question order).
   * @param {Object[]} calculatorResultsData
   * @param {string[][]} optionHandlesByStep
   * @param {string[]} prefixHandles
   * @param {number} stepIndex
   * @returns {Set<string>}
   */
  #getValidOptionHandlesForStep(
    calculatorResultsData,
    optionHandlesByStep,
    prefixHandles,
    stepIndex
  ) {
    const valid = new Set();
    if (!calculatorResultsData?.length) return valid;

    for (const result of calculatorResultsData) {
      const handlesPerStep = this.#assignOptionHandlesToSteps(
        result.selected_options,
        optionHandlesByStep
      );
      if (!handlesPerStep) continue;
      if (!this.#prefixHandlesMatch(handlesPerStep, prefixHandles, stepIndex)) continue;

      const handleAtStep = FormulaCalculatorOptions.normalizeOptionHandle(
        handlesPerStep[stepIndex]
      );
      if (handleAtStep) valid.add(handleAtStep);
    }
    return valid;
  }

  /**
   * @param {Object[]} calculatorResultsData
   * @param {Set<string>} selectedOptionHandles
   * @returns {Record<string, unknown> | null}
   */
  #findMatchingCalculatorResult(calculatorResultsData, selectedOptionHandles) {
    for (const result of calculatorResultsData) {
      const resultHandles = new Set(
        (result.selected_options || []).map((opt) =>
          FormulaCalculatorOptions.normalizeOptionHandle(opt.slug)
        )
      );
      if (
        selectedOptionHandles.size === resultHandles.size &&
        [...selectedOptionHandles].every((handle) => resultHandles.has(handle))
      ) {
        return result;
      }
    }
    return null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement[]}
   */
  #getCalculatorSteps(root) {
    return Array.from(root.querySelectorAll(SELECTORS.STEP));
  }

  /**
   * @param {DocumentFragment} clone
   * @param {Record<string, unknown>} resultData
   * @param {string[]} optionTitles
   */
  #fillCalculatorResultClone(clone, resultData, optionTitles) {
    const descEl = clone.querySelector('[data-formula-calculator-result-value]');
    if (descEl && resultData.result) descEl.innerHTML = resultData.result;

    clone.querySelectorAll('[data-question-index]').forEach((cell) => {
      const idx = parseInt(cell.dataset.questionIndex ?? '', 10);
      if (!Number.isNaN(idx) && optionTitles[idx]) {
        cell.textContent = optionTitles[idx];
      }
    });
  }

  connectedCallback() {
    this.addEventListener('click', this.#onOptionClick);
    this.addEventListener('keydown', this.#onOptionKeydown);
    this.#unsubscribeVariantChange = PubSub.subscribe('variant:change', ({ id }) => {
      if (!this.isConnected) return;
      const mainProductForm = document.querySelector('product-form');
      if (!mainProductForm || String(mainProductForm.dataset.productId) !== String(id)) return;
      queueMicrotask(() => this.#formulaCalculator());
    });
    queueMicrotask(() => this.#formulaCalculator());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onOptionClick);
    this.removeEventListener('keydown', this.#onOptionKeydown);
    this.#unsubscribeVariantChange?.();
    this.#unsubscribeVariantChange = null;
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getClickTargetOption(event) {
    const target = event.target;
    const element = target instanceof Element ? target : target?.parentElement;
    const option = element?.closest(SELECTORS.FORMULA_CALCULATOR_OPTION);
    if (!option || !this.contains(option)) return null;
    return option;
  }

  /**
   * @param {HTMLElement} option
   */
  #activateOption(option) {
    FormulaCalculatorOptions.applySelection(option);
    const step = option.closest(SELECTORS.STEP);
    const steps = this.#getCalculatorSteps(this);
    const stepIdx = step ? steps.indexOf(step) : -1;
    if (stepIdx >= 0) {
      this.#refilterStepsAfter(stepIdx);
    }
    this.refreshResultFromSelection();
  }

  #onOptionClick = (event) => {
    const option = this.#getClickTargetOption(event);
    if (!option) return;
    this.#activateOption(option);
  };

  #onOptionKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const option = this.#getClickTargetOption(event);
    if (!option) return;
    event.preventDefault();
    this.#activateOption(option);
  };

  /** Parses JSON from the script tag and applies variant-scoped filtering. */
  #loadFilteredCalculatorResults() {
    const raw = this.getcalculatorResultsData();
    this.calculatorResultsData = this.#filterResultsByVariant(
      raw,
      this.#getCurrentVariantIdFromDom()
    );
  }

  #formulaCalculator() {
    if (!this.querySelector(SELECTORS.STEP)) return;
    this.#loadFilteredCalculatorResults();

    const steps = this.#getCalculatorSteps(this);
    steps.forEach((step) => FormulaCalculatorOptions.getOptionsComponent(step)?.restoreSlides());
    FormulaCalculatorOptions.resetStyles(this);

    for (let i = 0; i < steps.length; i++) {
      steps[i].hidden = i > 0;
    }

    this.#filterStepOptions(0);

    this.syncSelectionsFromDom();
    this.showResults();
  }

  #buildOptionHandlesByStep() {
    return this.#getCalculatorSteps(this).map((step) => {
      const component = FormulaCalculatorOptions.getOptionsComponent(step);
      return component ? component.getAllOptionHandles() : [];
    });
  }

  #filterStepOptions(stepIndex) {
    const steps = this.#getCalculatorSteps(this);
    const step = steps[stepIndex];
    if (!step) return;

    const optionsComponent = FormulaCalculatorOptions.getOptionsComponent(step);
    if (!optionsComponent) return;

    optionsComponent.restoreSlides();

    if (!this.calculatorResultsData?.length) {
      return;
    }

    const optionHandlesByStep = this.#buildOptionHandlesByStep();
    const prefixHandles = [];
    for (let priorStepIndex = 0; priorStepIndex < stepIndex; priorStepIndex++) {
      prefixHandles.push(
        FormulaCalculatorOptions.getSelectedOptionHandleForStep(steps[priorStepIndex])
      );
    }

    const valid = this.#getValidOptionHandlesForStep(
      this.calculatorResultsData,
      optionHandlesByStep,
      prefixHandles,
      stepIndex
    );

    optionsComponent.applyAllowedOptions(valid);
  }

  /**
   * Clear and hide questions after the step the user just answered; refilter options; reveal next question when applicable.
   * @param {number} changedStepIndex
   */
  #refilterStepsAfter(changedStepIndex) {
    const steps = this.#getCalculatorSteps(this);

    for (let i = changedStepIndex + 1; i < steps.length; i++) {
      steps[i].hidden = true;
      FormulaCalculatorOptions.getOptionsComponent(steps[i])?.restoreSlides();
      FormulaCalculatorOptions.resetStyles(steps[i]);
    }

    for (let i = changedStepIndex + 1; i < steps.length; i++) {
      this.#filterStepOptions(i);
    }

    const hasSelection =
      FormulaCalculatorOptions.getSelectedOptionHandleForStep(steps[changedStepIndex]) !== '';
    if (hasSelection && changedStepIndex + 1 < steps.length) {
      steps[changedStepIndex + 1].hidden = false;
    }
  }

  /**
   * Rebuild selection arrays from the DOM (one entry per question, metaobject / Liquid order).
   */
  syncSelectionsFromDom() {
    this.selectedOptionHandles = [];
    this.selectedOptionTitles = [];
    for (const step of this.#getCalculatorSteps(this)) {
      if (step.hidden) break;
      const buttons = FormulaCalculatorOptions.listOptionButtons(step);
      const selected = buttons.find((button) =>
        button.classList.contains(FormulaCalculatorOptions.OPTION_SCHEME.selected)
      );
      if (!selected) break;
      const optionHandle = selected.dataset.formulaCalculatorOption ?? '';
      const title =
        selected.dataset.formulaCalculatorOptionTitle ?? optionHandle.replace(/-/g, ' ');
      this.selectedOptionHandles.push(optionHandle);
      this.selectedOptionTitles.push(title);
    }
  }

  refreshResultFromSelection() {
    this.syncSelectionsFromDom();
    this.showResults();
  }

  /** @returns {HTMLElement | null} */
  #getResultsMount() {
    return this.querySelector(SELECTORS.RESULTS_SLOT);
  }

  #removeDynamicResult() {
    this.querySelector(SELECTORS.DYNAMIC_RESULT)?.remove();
  }

  /**
   * @param {boolean} visible - When false, hides the no-match panel (e.g. before showing a recommendation).
   */
  #setNoResultVisible(visible) {
    const noResultEl = this.querySelector(SELECTORS.NO_RESULT);
    if (!noResultEl) return;
    if (visible) {
      noResultEl.removeAttribute('aria-hidden');
      noResultEl.removeAttribute('inert');
    } else {
      noResultEl.setAttribute('aria-hidden', 'true');
      noResultEl.setAttribute('inert', '');
    }
  }

  /**
   * @returns {Array<Record<string, unknown>>}
   */
  getcalculatorResultsData() {
    const script = this.querySelector('#formula-calculator-result-data');
    if (!script) return [];
    try {
      let json = script.textContent.trim();
      if (!json.startsWith('[')) json = `[${json}]`;
      return JSON.parse(json);
    } catch (e) {
      console.error('Formula calculator: Failed to parse calculator results JSON', e);
      return [];
    }
  }

  /**
   * @param {Record<string, unknown>} resultData
   * @param {string[]} optionTitles
   * @returns {DocumentFragment | null}
   */
  renderResult(resultData, optionTitles) {
    const template = this.querySelector('#formula-calculator-dynamic-result-template');
    if (!template) return null;

    const clone = template.content.cloneNode(true);
    this.#fillCalculatorResultClone(clone, resultData, optionTitles);

    const resultRoot = clone.querySelector(SELECTORS.FORMULA_CALCULATOR_RESULT);
    if (resultRoot) {
      resultRoot.setAttribute('aria-hidden', 'false');
      resultRoot.removeAttribute('inert');
      resultRoot.setAttribute('tabindex', '0');
      resultRoot.classList.add('formula-calculator-dynamic-result-rendered');
    }
    return clone;
  }

  showResults = () => {
    if (!this.calculatorResultsData) {
      this.#loadFilteredCalculatorResults();
    }

    const steps = this.#getCalculatorSteps(this);
    this.syncSelectionsFromDom();

    this.#removeDynamicResult();
    this.#setNoResultVisible(false);

    if (this.selectedOptionHandles.length < steps.length) {
      return;
    }

    const selectedHandlesSet = new Set(
      this.selectedOptionHandles.map((handle) =>
        FormulaCalculatorOptions.normalizeOptionHandle(handle)
      )
    );

    const matchingResult =
      this.calculatorResultsData?.length > 0
        ? this.#findMatchingCalculatorResult(this.calculatorResultsData, selectedHandlesSet)
        : null;

    if (matchingResult) {
      this.#appendMatchingResult(matchingResult);
      return;
    }

    this.#setNoResultVisible(true);
  };

  /**
   * @param {Record<string, unknown>} matchingResult
   */
  #appendMatchingResult(matchingResult) {
    const rendered = this.renderResult(matchingResult, this.selectedOptionTitles);
    const mount = this.#getResultsMount();
    if (!rendered || !mount) return;
    mount.appendChild(rendered);
  }
}

customElements.define('formula-calculator', FormulaCalculator);
